// src/components/GangRun/GangLinesEditor.js
// Editor de líneas de un Gang Run (PLAN-GANG-RUN). La unidad atómica es
// CLIENTE + SKU (del catálogo, que trae el 1UP/dieline) + cantidad pedida + ups.
// NO depende de un trabajo previo: puedes combinar clientes distintos en una hoja,
// y dar de alta cliente/SKU al vuelo. Se usa al crear y al editar (JobView).
import React, { useState, useMemo } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { Plus, Trash2, QrCode, Calculator, Users } from "lucide-react";
import { calcImposition } from "../../lib/impositions";
import { suggestSheets } from "../../lib/gangrun";
import { createProduct } from "../../lib/products";
import { downloadQrPng } from "../../utils/qrLabel";
import ClientModal from "../ClientModal";
import ProductModal from "../ProductModal";

const GangLinesEditor = ({ db, user, clients, products, categories, materials, sheet, lines, onChange, reload, storage, gangMaterialId }) => {
  const [np, setNp] = useState({ clientId: "", productId: "", orderedQty: "", ups: 1 });
  const [calc, setCalc] = useState({ show: false, pieceW: "", pieceH: "", margin: 0, gap: 0 });
  const [clientModal, setClientModal] = useState(false);
  const [productModal, setProductModal] = useState(false);
  const [err, setErr] = useState("");

  // Productos del catálogo: del cliente elegido (si tiene) + los genéricos.
  const availableProducts = useMemo(
    () => (products || []).filter((p) => p.active !== false && (!np.clientId || !p.clientId || p.clientId === np.clientId)),
    [products, np.clientId]
  );
  const selectedProduct = (products || []).find((p) => p.id === np.productId) || null;

  const calcResult = useMemo(() => calcImposition({
    pieceW: calc.pieceW, pieceH: calc.pieceH, sheetW: sheet?.width, sheetH: sheet?.height,
    margin: calc.margin, gap: calc.gap, qty: np.orderedQty,
  }), [calc, sheet, np.orderedQty]);

  const handleSaveClient = async (data) => {
    const ref = await addDoc(collection(db, "clients"), { ...data, createdAt: serverTimestamp() });
    if (reload) await reload();
    setNp((p) => ({ ...p, clientId: ref.id }));
    setClientModal(false);
  };
  const handleSaveProduct = async (data) => {
    const { id } = await createProduct(db, { ...data, clientId: data.clientId || np.clientId || null }, user);
    if (reload) await reload();
    setNp((p) => ({ ...p, productId: id }));
    setProductModal(false);
  };

  const addLine = () => {
    if (!np.clientId || !np.productId) return;
    const client = (clients || []).find((c) => c.id === np.clientId);
    const p = selectedProduct;
    // #1 Validación de material: el SKU debe ser del mismo material que el pliego del gang.
    if (gangMaterialId && p?.attributes?.materialId && p.attributes.materialId !== gangMaterialId) {
      setErr(`El SKU ${p.sku} es de otro material (${p.attributes.material || "?"}); no se puede correr en este pliego. Elige uno del mismo material.`);
      return;
    }
    setErr("");
    onChange([...(lines || []), {
      clientId: client?.id || null, clientName: client?.name || client?.companyName || "",
      skuRef: { skuId: p.id, version: p.version || 1 }, sku: p.sku, pieceCode: p.sku, pieceName: p.name,
      orderedQty: Number(np.orderedQty) || 0, ups: Number(np.ups) || 1, fulfillmentMode: "PTO",
    }]);
    setNp({ clientId: "", productId: "", orderedQty: "", ups: 1 });
  };
  const removeLine = (i) => onChange((lines || []).filter((_, idx) => idx !== i));
  const updateLine = (i, patch) => onChange((lines || []).map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const sheets = suggestSheets(lines);
  const clientsCount = new Set((lines || []).map((l) => l.clientId).filter(Boolean)).size;
  const inputCls = "p-2 border border-gray-300 rounded-md text-sm";

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-sm text-gray-600">
        {clientsCount > 1 && <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700"><Users size={12} /> Gang ({clientsCount} clientes)</span>}
        <span>Hojas sugeridas a correr: <b>{sheets}</b></span>
      </div>
      {err && <div className="mb-2 p-2 rounded bg-red-50 text-red-700 text-xs border border-red-200">{err}</div>}

      <table className="w-full text-sm">
        <thead className="text-xs text-gray-500 uppercase">
          <tr><th className="text-left py-1">Cliente</th><th className="text-left">SKU / Producto</th><th className="text-center">Pedido</th><th className="text-center">Ups</th><th className="text-center">Prod. estimada</th><th></th></tr>
        </thead>
        <tbody className="divide-y">
          {(lines || []).length === 0 && <tr><td colSpan="6" className="text-center text-gray-400 py-3">Sin líneas. Agrega un cliente + su SKU del catálogo.</td></tr>}
          {(lines || []).map((l, i) => (
            <tr key={i}>
              <td className="py-1">{l.clientName || "—"}</td>
              <td><span className="font-mono">{l.sku}</span>{l.pieceName ? <span className="text-gray-500"> · {l.pieceName}</span> : ""}</td>
              <td className="text-center"><input type="number" min="1" value={l.orderedQty} onChange={(e) => updateLine(i, { orderedQty: Number(e.target.value) || 0 })} className="w-20 p-1 border border-gray-300 rounded text-center" /></td>
              <td className="text-center"><input type="number" min="1" value={l.ups} onChange={(e) => updateLine(i, { ups: Number(e.target.value) || 1 })} className="w-16 p-1 border border-gray-300 rounded text-center" /></td>
              <td className="text-center">{(Number(l.ups) || 0) * sheets}</td>
              <td className="text-right whitespace-nowrap">
                <button type="button" onClick={() => downloadQrPng(JSON.stringify({ sku: l.sku, clientId: l.clientId, pieceCode: l.pieceCode }), l.sku, `${l.sku}_QR.png`)} title="Etiqueta QR de la pieza" className="text-gray-400 hover:text-emerald-600 mr-2"><QrCode size={16} /></button>
                <button type="button" onClick={() => removeLine(i)} title="Quitar" className="text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Agregar línea: Cliente + SKU del catálogo */}
      <div className="flex flex-wrap items-end gap-2 mt-3 pt-3 border-t">
        <div className="flex gap-1">
          <select value={np.clientId} onChange={(e) => setNp({ ...np, clientId: e.target.value, productId: "" })} className={inputCls + " min-w-[150px]"}>
            <option value="">— Cliente —</option>
            {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.name || c.companyName}</option>)}
          </select>
          <button type="button" onClick={() => setClientModal(true)} className="px-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm" title="Nuevo cliente">+</button>
        </div>
        <div className="flex gap-1">
          <select value={np.productId} onChange={(e) => setNp({ ...np, productId: e.target.value })} className={inputCls + " min-w-[180px]"}>
            <option value="">{availableProducts.length ? "— SKU / 1UP —" : "— sin SKU (crea uno) —"}</option>
            {availableProducts.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </select>
          <button type="button" onClick={() => setProductModal(true)} className="px-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm" title="Nuevo SKU/producto">+</button>
        </div>
        <input type="number" min="1" placeholder="Pedido" title="Cantidad pedida" value={np.orderedQty} onChange={(e) => setNp({ ...np, orderedQty: e.target.value })} className={inputCls + " w-24"} />
        <input type="number" min="1" placeholder="Ups" title="Ups en el pliego" value={np.ups} onChange={(e) => setNp({ ...np, ups: e.target.value })} className={inputCls + " w-20"} />
        <button type="button" onClick={() => setCalc({ ...calc, show: !calc.show })} title="Calculadora de imposición" className="px-2 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600"><Calculator size={16} /></button>
        <button type="button" onClick={addLine} disabled={!np.clientId || !np.productId} className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-1"><Plus size={16} /> Línea</button>
      </div>

      {calc.show && (
        <div className="mt-2 p-3 rounded-lg bg-gray-50 border flex flex-wrap items-end gap-2">
          <span className="text-xs font-semibold text-gray-500">🧮 Hoja {sheet?.width || "?"}×{sheet?.height || "?"} {sheet?.unit}:</span>
          <input type="number" placeholder="Ancho pieza" value={calc.pieceW} onChange={(e) => setCalc({ ...calc, pieceW: e.target.value })} className={inputCls + " w-28"} />
          <input type="number" placeholder="Alto pieza" value={calc.pieceH} onChange={(e) => setCalc({ ...calc, pieceH: e.target.value })} className={inputCls + " w-28"} />
          <input type="number" placeholder="Margen" value={calc.margin} onChange={(e) => setCalc({ ...calc, margin: e.target.value })} className={inputCls + " w-20"} />
          <input type="number" placeholder="Separación" value={calc.gap} onChange={(e) => setCalc({ ...calc, gap: e.target.value })} className={inputCls + " w-24"} />
          <div className="text-sm text-gray-700"><b>{calcResult.upsPerSheet}</b> ups/hoja ({calcResult.orientation}) · <b>{calcResult.sheets}</b> hojas · desperdicio {calcResult.wastePct}%</div>
          <button type="button" onClick={() => setNp({ ...np, ups: calcResult.upsPerSheet })} disabled={!calcResult.upsPerSheet} className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40">Usar ups</button>
        </div>
      )}

      <ClientModal isOpen={clientModal} onClose={() => setClientModal(false)} onSave={handleSaveClient} />
      <ProductModal isOpen={productModal} onClose={() => setProductModal(false)} onSave={handleSaveProduct} productData={null} categories={categories} clients={clients} materials={materials} storage={storage} />
    </div>
  );
};

export default GangLinesEditor;
